// src/components/property/PropertyBulkActions.tsx
/**
 * PropertyBulkActions Component
 * Bulk action buttons for scene planning, generation, and muxing
 */

import { memo } from 'react';
import { Wand2, Mic, Video, Loader2, AudioLines } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import type { IPropertyBulkActionsProps, IBulkProgress } from '@/@types/propertySceneEditor';

// ============================================================================
// Progress Display Component
// ============================================================================

interface IProgressDisplayProps {
  progress: IBulkProgress;
  label: string;
}

function ProgressDisplay({ progress, label }: IProgressDisplayProps) {
  if (progress.status === 'idle') return null;

  const percent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;

  const statusText =
    progress.status === 'generating'
      ? `Generating ${label.toLowerCase()}: ${progress.completed} of ${progress.total}`
      : progress.status === 'completed'
        ? `${label} generation complete`
        : progress.status === 'partial'
          ? `${label} generation partially complete, ${progress.failed} failed`
          : progress.status === 'failed'
            ? `${label} generation failed`
            : '';

  return (
    <div className="flex items-center gap-3 min-w-[200px]" role="status" aria-live="polite">
      <div className="flex-1">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>{label}</span>
          <span aria-hidden="true">
            {progress.completed}/{progress.total}
          </span>
        </div>
        <Progress value={percent} className="h-2" aria-label={`${label} progress: ${percent}%`} />
        <span className="sr-only">{statusText}</span>
      </div>
      {progress.status === 'generating' && (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
      )}
      {progress.status === 'completed' && <span className="text-xs text-green-600">Done</span>}
      {progress.status === 'partial' && (
        <span className="text-xs text-yellow-600">{progress.failed} failed</span>
      )}
      {progress.status === 'failed' && <span className="text-xs text-destructive">Failed</span>}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function PropertyBulkActions({
  hasScenes,
  hasExistingVoiceovers,
  voiceoverProgress,
  videoProgress,
  muxProgress,
  allVoiceoversReady,
  allVideosReady,
  onPlanScenes,
  onGenerateAllVoiceovers,
  onRegenerateAllVoiceovers,
  onGenerateAllVideos,
  onMuxAllScenes,
  isPlanningScenes,
}: IPropertyBulkActionsProps) {
  const isVoiceoverGenerating = voiceoverProgress.status === 'generating';
  const isVideoGenerating = videoProgress.status === 'generating';
  const isMuxing = muxProgress.status === 'generating';
  const isAnyGenerating =
    isPlanningScenes || isVoiceoverGenerating || isVideoGenerating || isMuxing;

  // Use regenerate when voiceovers already exist
  const handleVoiceoverClick = hasExistingVoiceovers
    ? onRegenerateAllVoiceovers
    : onGenerateAllVoiceovers;

  const voiceoverButtonLabel = hasExistingVoiceovers
    ? 'Regenerate All Voiceovers'
    : 'Generate All Voiceovers';

  return (
    <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/30 rounded-lg border">
      {/* Plan Scenes Button */}
      <Button
        onClick={onPlanScenes}
        disabled={isAnyGenerating}
        variant={hasScenes ? 'outline' : 'default'}
      >
        {isPlanningScenes ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Wand2 className="h-4 w-4 mr-2" />
        )}
        {hasScenes ? 'Replan Scenes' : 'Plan Scenes'}
      </Button>

      {/* Generate/Regenerate All Voiceovers Button */}
      <Button
        onClick={handleVoiceoverClick}
        disabled={!hasScenes || isAnyGenerating}
        variant="outline"
      >
        {isVoiceoverGenerating ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Mic className="h-4 w-4 mr-2" />
        )}
        {voiceoverButtonLabel}
      </Button>

      {/* Generate All Videos Button */}
      <Button
        onClick={onGenerateAllVideos}
        disabled={!allVoiceoversReady || isAnyGenerating}
        variant="outline"
      >
        {isVideoGenerating ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Video className="h-4 w-4 mr-2" />
        )}
        Generate All Videos
      </Button>

      {/* Mux All Scenes Button - combines video + audio */}
      <Button
        onClick={onMuxAllScenes}
        disabled={!allVideosReady || isAnyGenerating}
        variant="outline"
        title="Combine video and voiceover audio for each scene"
      >
        {isMuxing ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <AudioLines className="h-4 w-4 mr-2" />
        )}
        Mux All Scenes
      </Button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Progress Indicators */}
      <ProgressDisplay progress={voiceoverProgress} label="Voiceovers" />
      <ProgressDisplay progress={videoProgress} label="Videos" />
      <ProgressDisplay progress={muxProgress} label="Muxing" />
    </div>
  );
}

export const PropertyBulkActionsMemo = memo(PropertyBulkActions);
